import { useCallback, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ApiError, api } from "@/lib/api";
import { driveRun, streamMaxwell } from "@/lib/maxwell-stream";
import type {
  ActionPreviewState,
  Guidance,
  MaxwellCommitResponse,
  MaxwellMessage,
  MaxwellPart,
  MaxwellSubject,
  MaxwellThread,
  MaxwellThreadSummary,
  RunStepProgress,
} from "@/types/maxwell";

const MAXWELL_KEY = ["maxwell", "thread"] as const;
const THREADS_KEY = ["maxwell", "threads"] as const;

function threadKey(subject: MaxwellSubject | null) {
  return [...MAXWELL_KEY, subject?.type ?? null, subject?.id ?? null] as const;
}

/** Cache key for one specific thread — the entry a pinned or history-switched
 *  conversation renders through. Exported so `MaxwellChat` can seed it at pin
 *  time with the thread already on screen. */
export function maxwellThreadByIdKey(threadId: string | null) {
  return [...MAXWELL_KEY, "by-id", threadId] as const;
}

function threadsKey(subject: MaxwellSubject | null) {
  return [...THREADS_KEY, subject?.type ?? null, subject?.id ?? null] as const;
}

/** Get-or-create the caller's most-recent thread for the current subject. */
export function useMaxwellThread(subject: MaxwellSubject | null) {
  return useQuery({
    queryKey: threadKey(subject),
    enabled: subject !== null,
    queryFn: () => {
      const params = new URLSearchParams({ subject_type: subject!.type });
      if (subject!.id) params.set("subject_id", subject!.id);
      return api.get<MaxwellThread>(`/api/maxwell/thread?${params.toString()}`);
    },
  });
}

/** Load one specific thread (with its messages) by id — backs history switch. */
export function useMaxwellThreadById(threadId: string | undefined) {
  return useQuery({
    queryKey: maxwellThreadByIdKey(threadId ?? null),
    enabled: !!threadId,
    queryFn: () => api.get<MaxwellThread>(`/api/maxwell/thread/${threadId}`),
  });
}

/** The caller's non-archived thread history for the current subject. */
export function useMaxwellThreads(subject: MaxwellSubject | null) {
  return useQuery({
    queryKey: threadsKey(subject),
    enabled: subject !== null,
    queryFn: () => {
      const params = new URLSearchParams({ subject_type: subject!.type });
      if (subject!.id) params.set("subject_id", subject!.id);
      return api.get<MaxwellThreadSummary[]>(`/api/maxwell/threads?${params.toString()}`);
    },
  });
}

/** Pull the typed `Guidance` shape off a tool result. */
function asGuidanceFromResult(result: unknown): Guidance | null {
  if (result && typeof result === "object" && "step_total" in result) {
    return result as Guidance;
  }
  return null;
}

/** Narrow a streamed tool result into the typed `Guidance` shape. */
export function asGuidance(result: unknown): Guidance | null {
  return asGuidanceFromResult(result);
}

/** Pull the in-app route off a `navigate` tool result, if present. */
function routeFromResult(result: unknown): string | null {
  if (result && typeof result === "object" && "route" in result) {
    const route = (result as { route: unknown }).route;
    if (typeof route === "string" && route.startsWith("/")) return route;
  }
  return null;
}

/** Pull the started run's id off a `start_agent_run` tool result, if present. */
function runIdFromResult(result: unknown): string | null {
  if (result && typeof result === "object" && "run_id" in result) {
    const runId = (result as { run_id: unknown }).run_id;
    if (typeof runId === "string" && runId.length > 0) return runId;
  }
  return null;
}

/** Whether a `start_agent_run` tool result marks the run as in-chat driven —
 *  the chat surface then opens the drive stream and steps it live. */
function isInChatDriver(result: unknown): boolean {
  return (
    typeof result === "object" &&
    result !== null &&
    (result as { driver?: unknown }).driver === "in_chat"
  );
}

let optimisticSeq = 0;
function optimisticId(prefix: string): string {
  optimisticSeq += 1;
  return `optimistic:${prefix}:${optimisticSeq}`;
}

/**
 * Send a message and stream the reply. Optimistically inserts the user turn
 * (and a growing assistant placeholder) into the React Query cache so the
 * transcript never blanks; reconciles against the refetched server thread on
 * completion. This is the fix for the disappearing-message bug — the UI no
 * longer renders only stale cache + a separate draft string.
 */
export function useSendMaxwellMessage(
  threadId: string | undefined,
  subject: MaxwellSubject | null,
) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [lastGuidance, setLastGuidance] = useState<Guidance | null>(null);
  const [pendingPreview, setPendingPreview] = useState<ActionPreviewState | null>(null);
  // Keyed by the thread_id this hook was sending on when the run started, so a
  // run started while chatting about one subject never bleeds into another
  // thread's transcript after navigation (the hook's state outlives the
  // thread/subject switch — `MaxwellChat` is never remounted).
  const [activeRuns, setActiveRuns] = useState<{ runId: string; threadId: string }[]>([]);
  // Live per-step progress of in-chat-driven runs (fed by the drive stream).
  const [runProgress, setRunProgress] = useState<Record<string, RunStepProgress[]>>({});
  // One drive stream per run; promote aborts it so the worker takes over alone.
  const driveAborts = useRef<Map<string, AbortController>>(new Map());
  const key = threadKey(subject);

  /** Open the synchronous drive stream for an in-chat run and mirror its step
   *  frames into `runProgress`; on any terminal frame, refetch the thread so
   *  the answer message the runner posted appears in the transcript. */
  const startDriving = useCallback(
    (runId: string) => {
      if (driveAborts.current.has(runId)) return;
      const controller = new AbortController();
      driveAborts.current.set(runId, controller);
      const refresh = () => {
        void qc.invalidateQueries({ queryKey: MAXWELL_KEY });
        void qc.invalidateQueries({ queryKey: ["maxwell", "runs"] });
      };
      driveRun(
        runId,
        (ev) => {
          switch (ev.kind) {
            case "step":
              setRunProgress((prev) => {
                const steps = prev[runId] ?? [];
                const next = steps.filter((s) => s.step_index !== ev.step_index);
                next.push({
                  step_index: ev.step_index,
                  status: ev.status,
                  capability: ev.capability,
                });
                next.sort((a, b) => a.step_index - b.step_index);
                return { ...prev, [runId]: next };
              });
              break;
            case "done":
            case "paused":
            case "promoted":
              refresh();
              break;
            case "error":
              toast.error(ev.message || "The run hit a problem.");
              refresh();
              break;
          }
        },
        controller.signal,
      )
        .catch((err) => {
          // An abort is the promote path, not a failure.
          if (!controller.signal.aborted) {
            toast.error(err instanceof ApiError ? err.message : "Lost the run stream.");
          }
        })
        .finally(() => {
          driveAborts.current.delete(runId);
          refresh();
        });
    },
    [qc],
  );

  /** Hand an in-chat run to the background worker: stop the local drive stream
   *  and flip the run's driver server-side. */
  const promoteRun = useCallback(
    async (runId: string) => {
      driveAborts.current.get(runId)?.abort();
      await api.post(`/api/maxwell/runs/${runId}/promote`, {});
      void qc.invalidateQueries({ queryKey: ["maxwell", "runs"] });
    },
    [qc],
  );

  const mutation = useMutation({
    mutationFn: async (text: string) => {
      if (!threadId) return;
      // A fresh turn clears any prior unresolved write preview.
      setPendingPreview(null);

      const userId = optimisticId("user");
      const assistantId = optimisticId("assistant");

      // Apply a live update everywhere THIS thread renders from: the
      // subject-keyed get-or-create entry AND the by-id entry (a pinned or
      // history-switched conversation reads through the latter). Guarded on
      // the thread id, so a subject cache that meanwhile holds a different
      // thread (the user navigated away mid-conversation) is never
      // contaminated with this thread's messages.
      const updateThreadCaches = (update: (prev: MaxwellThread) => MaxwellThread) => {
        for (const cacheKey of [key, maxwellThreadByIdKey(threadId)]) {
          qc.setQueryData<MaxwellThread>(cacheKey, (prev) =>
            prev && prev.thread_id === threadId ? update(prev) : prev,
          );
        }
      };

      // Optimistically push the user turn + an empty assistant placeholder.
      updateThreadCaches((prev) => {
        const userMsg: MaxwellMessage = {
          id: userId,
          role: "user",
          parts: [{ kind: "text", text }],
          created_at: new Date().toISOString(),
        };
        const assistantMsg: MaxwellMessage = {
          id: assistantId,
          role: "assistant",
          parts: [{ kind: "text", text: "" }],
          created_at: new Date().toISOString(),
        };
        return { ...prev, messages: [...prev.messages, userMsg, assistantMsg] };
      });

      const appendAssistantText = (delta: string) => {
        updateThreadCaches((prev) => ({
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== assistantId) return m;
            const parts = m.parts.map((p): MaxwellPart =>
              p.kind === "text" ? { kind: "text", text: p.text + delta } : p,
            );
            return { ...m, parts };
          }),
        }));
      };

      await streamMaxwell(threadId, text, (ev) => {
        switch (ev.kind) {
          case "text":
            appendAssistantText(ev.delta);
            break;
          case "tool": {
            if (ev.name === "navigate") {
              const route = routeFromResult(ev.result);
              if (route) navigate(route);
              break;
            }
            if (ev.name === "start_agent_run") {
              const runId = runIdFromResult(ev.result);
              if (runId && threadId) {
                setActiveRuns((runs) =>
                  runs.some((r) => r.runId === runId) ? runs : [...runs, { runId, threadId }],
                );
                // An in-chat run is driven by THIS surface: open the drive
                // stream so its steps execute and play out live right here.
                if (isInChatDriver(ev.result)) startDriving(runId);
              }
            }
            const g = asGuidanceFromResult(ev.result);
            if (g) setLastGuidance(g);
            break;
          }
          case "blocks":
            updateThreadCaches((prev) => ({
              ...prev,
              messages: prev.messages.map((m) =>
                m.id === assistantId
                  ? { ...m, parts: [...m.parts, { kind: "blocks", blocks: ev.blocks }] }
                  : m,
              ),
            }));
            break;
          case "action_preview":
            // A write paused for approval — surface the confirm card. The turn
            // ends here; the user approves via the commit endpoint.
            setPendingPreview({
              capability: ev.capability,
              summary: ev.summary,
              details: ev.details,
              commit_token: ev.commit_token,
              input: ev.input,
              status: "pending",
            });
            break;
          case "done":
            break;
          case "error":
            toast.error(ev.message || "Maxwell ran into a problem.");
            break;
        }
      });
    },
    onSettled: () => {
      // Reconcile: refetch the persisted thread. The optimistic entries
      // (`optimistic:*` ids) are replaced wholesale by the server's messages.
      void qc.invalidateQueries({ queryKey: key });
      // A pinned or history-switched conversation renders through the by-id
      // query — a sibling key the subject invalidation above doesn't reach.
      // Without this the transcript freezes on its pre-send snapshot.
      void qc.invalidateQueries({ queryKey: maxwellThreadByIdKey(threadId ?? null) });
      void qc.invalidateQueries({ queryKey: threadsKey(subject) });
    },
  });

  const send = useCallback(
    (text: string) => {
      mutation.mutate(text);
    },
    [mutation],
  );

  return {
    send,
    streaming: mutation.isPending,
    lastGuidance,
    pendingPreview,
    setPendingPreview,
    activeRuns,
    runProgress,
    promoteRun,
  };
}

/**
 * Approve + commit a previewed Maxwell write. The body echoes the exact
 * `capability` + `input` the `action_preview` frame carried, plus the
 * `commit_token`; the server recomputes the token and rejects on mismatch.
 */
export function useCommitMaxwellAction(
  threadId: string | undefined,
  subject: MaxwellSubject | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { capability: string; input: unknown; commit_token: string }) =>
      api.post<MaxwellCommitResponse>(`/api/maxwell/thread/${threadId}/commit`, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: threadKey(subject) });
    },
  });
}

/** "New chat": create a fresh thread and switch the active-thread cache to it. */
export function useNewMaxwellThread(subject: MaxwellSubject | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<MaxwellThread>("/api/maxwell/threads", {
        subject_type: subject?.type,
        subject_id: subject?.id ?? undefined,
      }),
    onSuccess: (thread) => {
      qc.setQueryData<MaxwellThread>(threadKey(subject), thread);
      void qc.invalidateQueries({ queryKey: threadsKey(subject) });
    },
  });
}

/** Rename a thread in the history list. */
export function useRenameMaxwellThread(subject: MaxwellSubject | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      api.patch<void>(`/api/maxwell/threads/${threadId}`, { title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadsKey(subject) });
    },
  });
}

/** Archive a thread (soft-hide from history). */
export function useArchiveMaxwellThread(subject: MaxwellSubject | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => api.del<void>(`/api/maxwell/threads/${threadId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadsKey(subject) });
      void qc.invalidateQueries({ queryKey: threadKey(subject) });
    },
  });
}
